import { useRef } from 'react';

import { useAtomValue } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import Button from '@gds/Button/Button';
import { Input } from '@gds/Input/Input';
import Slider from '@gds/Slider/Slider';
import { Stepper } from '@gds/Stepper/Stepper';
import { Typography } from '@gds/Typography/Typography';
import { useRangeFilters } from '@search-filters/Hooks/UseRangeFilters';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import { TEST_RANGE } from './YearFilter.constants';

import piStyles from '../PairedInputs/PairedInputs.module.css';
import styles from './YearFilter.module.css';

type YearFilterProps = FilterControlBaseProps;

export const YearFilter = ({ strings, tenant }: YearFilterProps) => {
  const minAvailable = TEST_RANGE[0].toString();
  const maxAvailable = TEST_RANGE[TEST_RANGE.length - 1].toString();
  const { totalCount } = useAtomValue(aggregationsAtom);

  const { gteInputProps, lteInputProps, sliderProps, canReset, reset, stepGte, stepLte } =
    useRangeFilters('year', TEST_RANGE, [minAvailable, maxAvailable]);

  const gteInputRef = useRef<HTMLInputElement>(null);
  const lteInputRef = useRef<HTMLInputElement>(null);

  return (
    <section className={styles.yearFilter}>
      <Typography className={styles.infoLine} variant="subheading4">
        {strings.yearInfoLine}
      </Typography>
      <div className={piStyles.pairedInputs}>
        <Input
          {...gteInputProps}
          ref={gteInputRef}
          label={strings.min}
          type="number"
          min={minAvailable}
          rightAddons={
            <Stepper
              label="minimum year"
              focusTargetRef={gteInputRef}
              onIncrement={() => stepGte(1)}
              onDecrement={() => stepGte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
        <Input
          {...lteInputProps}
          ref={lteInputRef}
          label={strings.max}
          type="number"
          max={maxAvailable}
          rightAddons={
            <Stepper
              label="maximum year"
              focusTargetRef={lteInputRef}
              onIncrement={() => stepLte(1)}
              onDecrement={() => stepLte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({totalCount.toLocaleString(ukLocale)})
        </Typography>
        <Button
          color="secondary"
          disabled={!canReset(['stock-condition'])}
          onClick={() => reset(['stock-condition'])}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} className={styles.priceRangeSlider} dataTestId="year-slider" />
    </section>
  );
};
